import { useState, useCallback, useEffect, useMemo } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import styled, { createGlobalStyle } from 'styled-components'
import Header from '@/components/shardLibrary/Header'
import Footer from '@/components/shardLibrary/Footer'
import Collections from '@/components/shardLibrary/Collections'
import useRequest from '@/hooks/useRequest'
import { useLegacyCollections } from '@/hooks/useLegacyCollections'
import { Container, Div } from '@/styled/common/loading'
import useResizeObserver from 'use-resize-observer/polyfilled'
import { debounce } from 'lodash'
import LoadingAnimation from '@/components/LoadingAnimation'

const GlobalStyle = createGlobalStyle`
  body {
    font-family: proxima nova !important;
  }
`

const Page = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0px;
  top: 0px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  flex-direction: column;
  padding: min(30px, max(10px, min(2vw, 2vh)));
  background-color: var(--ggBackground);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  font-family: proxima nova;

  &, * {
    box-sizing: border-box;
  }
`

const ROW_HEIGHT = 258
const COLLECTION_WIDTH = 170

const ShardLibrary = (props: any) => {
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [loading, setLoading] = useState(true)
  const [totalCollectionsCount, setTotalCollectionsCount] = useState<number | null>(null)

  const resizeOfContent = useMemo(() =>
    debounce(({ width, height }: { width?: number; height?: number }) => {
      if(!width || !height) {
        return;
      }
      const gridGap = 40
      let columns = parseInt(String(width / COLLECTION_WIDTH))
      columns = parseInt(String(width / (COLLECTION_WIDTH + (gridGap * (columns - 1) / columns))))
      let rows = 2
      if (height < (2 * ROW_HEIGHT) + gridGap) rows = 1
      setPageSize(Math.round(columns * rows) || 1)
    }, 250),
  [setPageSize])

  const legacyCollections = useLegacyCollections()
  const { ref } = useResizeObserver({ onResize: resizeOfContent })

  const totalPages = useMemo(() => {
    let pageCounts = 1
    if (totalCollectionsCount || legacyCollections.length > 0) {
      pageCounts = Math.ceil(totalCollectionsCount! / pageSize)
    }
    return pageCounts
  }, [pageSize, totalCollectionsCount, legacyCollections])

  const collectionOffset = useMemo(() => {
    let offset = 0
    if (page) offset = page * pageSize
    return offset - legacyCollections.length
  }, [page, pageSize, legacyCollections])

  const updateCollectionDataUrl = useCallback(() => {
    const legacyCollectionsIDs = legacyCollections?.map((lc: any) => lc.id)?.join(',')
    return `/users/@me/collections?limit=${collectionOffset < 0 ? pageSize + collectionOffset : pageSize}&offset=${collectionOffset}&id__in!=${legacyCollectionsIDs}`
  }, [pageSize, collectionOffset, legacyCollections])

  const { data: userCollectionsData } = useRequest({ url: updateCollectionDataUrl() })
  const { data: notInCollectionCountData } = useRequest({ url: '/users/@me/shards/count?collection__id__isnull=True' })

  useEffect(() => {
    if (userCollectionsData && notInCollectionCountData) {
      setLoading(false)
      setTotalCollectionsCount(userCollectionsData.count + legacyCollections.length + 1)
    }
  }, [userCollectionsData, notInCollectionCountData])

  const redirectToCollection = useCallback((id: string | number) => {
    navigate({
      to: '/shardlibrary/$collectionId',
      params: { collectionId: String(id) },
      search: { source: undefined }
    })
  }, [navigate])

  const sortCollectionsForLegacy = useMemo(() => {
    if (userCollectionsData?.results) {
      const toMerge: any[] = []
      const legacyCollectionIds: any[] = []
      let filteredCollections = userCollectionsData.results

      if (page * pageSize < legacyCollections.length) {
        const indexOfBeginningOfPage = page * pageSize
        const indexOfEndingOfPage = (page + 1) * pageSize

        legacyCollections.slice(indexOfBeginningOfPage, indexOfEndingOfPage).forEach((legacyCollection: any) => {
          legacyCollectionIds.push(legacyCollection.id)
          const foundCollection = userCollectionsData?.results?.find((c: any) => c.id === legacyCollection.id)
          if (foundCollection) {
            toMerge.push(foundCollection)
            filteredCollections = filteredCollections.filter((c: any) => c.id !== legacyCollection.id)
          } else {
            toMerge.push(legacyCollection)
          }
        })
      } else {
        legacyCollections.forEach((legacyCollection: any) => {
          const foundCollection = userCollectionsData?.results?.find((c: any) => c.id === legacyCollection.id)
          if (foundCollection) {
            filteredCollections = filteredCollections.filter((c: any) => c.id !== legacyCollection.id)
          }
        })
      }

      filteredCollections.sort((a: any, b: any) =>
        a.shard_count < b.shard_count ? 1 : a.shard_count === b.shard_count ? (a.name > b.name ? 1 : -1) : -1
      )

      if (notInCollectionCountData?.count > 0 && page === totalPages - 1) {
        filteredCollections = [
          ...filteredCollections,
          { id: 0, name: 'Not In Collection', cover_url: null, shard_count: notInCollectionCountData.count },
        ]
      }

      let slicedFilteredCollection = filteredCollections
      if (collectionOffset < 0 && legacyCollections.length < pageSize) {
        slicedFilteredCollection = filteredCollections.slice(0, pageSize - legacyCollections.length)
      } else if (collectionOffset < 0 && legacyCollections.length >= pageSize) {
        slicedFilteredCollection = []
      }

      return { collections: [...toMerge, ...slicedFilteredCollection], legacyCollectionIds }
    }
    return []
  }, [userCollectionsData?.results, page, pageSize, legacyCollections, notInCollectionCountData?.count, totalPages, collectionOffset])

  return (
    <>
      <GlobalStyle />
      <Page>
        {loading ? (
          <Container className={props.className || 'Loading'} style={props.style} fixed={props.fixed}>
            <LoadingAnimation />
            {props.message && <Div>{props.message}</Div>}
          </Container>
        ) : (
          <>
            <Header collectionName="Collections" />
            <Collections
              contentRef={ref}
              collections={(sortCollectionsForLegacy as any).collections || []}
              legacyCollectionIds={(sortCollectionsForLegacy as any).legacyCollectionIds}
              redirectToCollection={redirectToCollection}
              rowHeight={ROW_HEIGHT}
            />
            {totalPages > 1 && <Footer totalPages={totalPages} currentPage={page} setPage={setPage} />}
          </>
        )}
      </Page>
    </>
  )
}

export const Route = createFileRoute('/_auth/shardlibrary/')({
  component: ShardLibrary,
})
